import { isAnyOf } from "@reduxjs/toolkit";

import { agenticLogicSystemRuleQueryOptions } from "@src/api/agentic-logic/ensure-agentic-logic-system-rule";
import { queryClient } from "@src/queryClient";
import type { AppListenerMiddleware } from "@src/store/rootState";
import {
  bindCheckoutUpsellDefaultRule,
  changeCheckoutTemplateType,
  copyCheckoutBlock,
  initNewCheckoutBlock,
} from "@src/store/slices/checkoutBlocks";
import { blockTakesCheckoutUpsellDefaultRule } from "@src/store/slices/checkoutBlocks/selectors";
import { getIsAgenticLogicEnabled } from "@src/store/slices/organization/selectors/getIsAgenticLogicEnabled";

/**
 * Binds a NEW Checkout > Upsell block to the store's built-in Shopify
 * recommendations rule, its default, where the block comes into being: added,
 * switched to an upsell template, or copied. Owning the default here rather
 * than in the block's settings panel means a block added and saved without
 * that panel ever mounting is still bound.
 *
 * The rule id arrives asynchronously — the PUT that reads it provisions the
 * store's copy on first call — so the bind is dispatched when it lands. The
 * reducer re-checks the block then, so a rule chosen in the meantime wins.
 */
export function setupCheckoutBlocksListener(middleware: AppListenerMiddleware) {
  middleware.startListening({
    matcher: isAnyOf(
      initNewCheckoutBlock,
      changeCheckoutTemplateType,
      copyCheckoutBlock
    ),
    effect: async (action, { getState, dispatch }) => {
      const state = getState();
      const checkoutBlockId = changeCheckoutTemplateType.match(action)
        ? action.payload.checkoutBlockId
        : copyCheckoutBlock.match(action)
          ? action.payload.checkoutBlock.id
          : state.checkoutBlocks.currentCheckoutBlockId;
      if (
        !checkoutBlockId ||
        !getIsAgenticLogicEnabled(state) ||
        !blockTakesCheckoutUpsellDefaultRule(
          state.checkoutBlocks,
          checkoutBlockId
        )
      ) {
        return;
      }
      const { rule } = await queryClient.ensureQueryData(
        agenticLogicSystemRuleQueryOptions("shopify-recommendations")
      );
      dispatch(
        bindCheckoutUpsellDefaultRule({
          checkoutBlockId,
          agenticLogicRuleId: rule.id,
        })
      );
    },
  });
}
